/* eslint-disable no-unused-vars */
import { useState } from 'react';
import { apiService } from 'services/service';
import {
    useGetCountries,
    useGetStatesByCountryId,
    useGetCitiesByStateId,
} from 'hooks/useGetLocations';
import {
    BiBriefcase,
    BiCalendar,
    BiCheckCircle,
    BiChevronRight,
    BiGlobe,
    BiHash,
    BiMailSend,
    BiMapPin,
    BiPhone,
    BiShoppingBag,
    BiUser,
    BiX,
} from 'react-icons/bi';
import { BsArrowLeft } from 'react-icons/bs';
import { LuBuilding2 } from 'react-icons/lu';
import { FiAlertCircle } from 'react-icons/fi';

const STEPS = ['Identity', 'Location', 'Profile', 'Review'];
const TITLES = ['Mr', 'Mrs', 'Ms'];
const OPERATIONAL_MODES = ['Online', 'Offline', 'Hybrid'];

const genTempPassword = () => {
    const words = ['Obana', 'Africa', 'Tajiri', 'Shop', 'Style'];
    const word = words[Math.floor(Math.random() * words.length)];
    const num = Math.floor(1000 + Math.random() * 9000);
    const sym = ['#', '@', '!'][Math.floor(Math.random() * 3)];
    return `${word}${sym}${num}`;
};

const buildPayload = (form, tempPassword, salesPerson) => ({
    email: form.email,
    password: tempPassword,
    phone: form.phone,
    attributes: {
        first_name: form.first_name,
        last_name: form.last_name,
        contact_type: 'customer',
        customer_sub_type: 'individual',
        billing_address: form.address || '',
        billing_city: form.city || '',
        billing_state: form.state || '',
        billing_country: form.country || 'Nigeria',
        address: form.address || '',
        city: form.city || '',
        state: form.state || '',
        country: form.country || 'Nigeria',
        language_code: 'en',
        account_types: 'individual',
        salutation: form.salutation || '',
        company_name: form.company_name || '',
        dob: form.dob || '',
        website: form.website || '',
        notes: form.notes || '',
        currency: 'NGN',
        referral_source: 'Salesperson',
        lead: salesPerson?.displayName || '',
        operational_mode: form.operationalMode || '',
        category_of_interest: '',
        brand_of_interest: '',
    },
    created_by_admin: true,
    return: 1,
});

const StepBar = ({ current }) => (
    <div style={styles.stepBar}>
        {STEPS.map((label, i) => (
            <div
                key={label}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: i < STEPS.length - 1 ? 1 : 'none',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div
                        style={{
                            ...styles.stepCircle,
                            background:
                                i < current ? '#16a34a' : i === current ? '#1b3b5f' : '#e2e8f0',
                            color: i <= current ? '#fff' : '#94a3b8',
                        }}>
                        {i < current ? <BiCheckCircle size={12} /> : i + 1}
                    </div>
                    <span
                        style={{
                            fontSize: 11,
                            fontWeight: i === current ? 700 : 500,
                            color: i === current ? '#1b3b5f' : i < current ? '#16a34a' : '#94a3b8',
                            whiteSpace: 'nowrap',
                        }}>
                        {label}
                    </span>
                </div>
                {i < STEPS.length - 1 && (
                    <div
                        style={{
                            flex: 1,
                            height: 1,
                            background: i < current ? '#86efac' : '#e2e8f0',
                            margin: '0 8px',
                        }}
                    />
                )}
            </div>
        ))}
    </div>
);

const SectionHead = ({ icon, title }) => (
    <div style={styles.sectionHead}>
        <span style={{ color: '#1b3b5f' }}>{icon}</span>
        <span style={styles.sectionTitle}>{title}</span>
    </div>
);

const F = ({ label, children, required, col, error }) => (
    <div style={{ gridColumn: col }}>
        <label style={styles.label}>
            {label}
            {required && <span style={{ color: '#dc2626' }}> *</span>}
        </label>
        {children}
        {error && <p style={styles.errText}>{error}</p>}
    </div>
);

const I = ({ value, onChange, placeholder, type = 'text', icon, disabled }) => (
    <div style={{ position: 'relative' }}>
        {icon && <span style={styles.iconWrap}>{icon}</span>}
        <input
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            style={{
                ...styles.input,
                paddingLeft: icon ? 30 : 10,
                background: disabled ? '#f8fafc' : '#fff',
            }}
        />
    </div>
);

/* Cascading select — locks with a 🔒 hint and muted style when disabled.
   options should be [{ value, label }]                                       */
const CascadeSel = ({ value, onChange, options, placeholder, disabled, loading }) => {
    const noOptions = !options || options.length === 0;
    const isDisabled = disabled || (noOptions && !loading);

    return (
        <div style={{ position: 'relative' }}>
            <select
                value={value}
                onChange={onChange}
                disabled={isDisabled}
                style={{
                    ...styles.input,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    background: isDisabled ? '#f8fafc' : '#fff',
                    color: isDisabled ? '#94a3b8' : '#374151',
                    appearance: 'auto',
                }}>
                <option value="">{loading ? 'Loading…' : placeholder}</option>
                {(options || []).map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
            {isDisabled && !loading && (
                <span
                    style={{
                        position: 'absolute',
                        right: 28,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: 11,
                        color: '#cbd5e1',
                        pointerEvents: 'none',
                    }}>
                </span>
            )}
        </div>
    );
};

const Sel = ({ value, onChange, options, placeholder }) => (
    <select value={value} onChange={onChange} style={{ ...styles.input, cursor: 'pointer' }}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
            <option key={o.value ?? o} value={o.value ?? o}>
                {o.label ?? o}
            </option>
        ))}
    </select>
);

const Grid = ({ cols = 2, children }) => (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
        {children}
    </div>
);

const LockedSP = ({ salesPerson }) => (
    <div style={styles.lockedSP}>
        <div style={styles.spAvatar}>{salesPerson?.first_name?.[0]?.toUpperCase() || 'S'}</div>
        <div>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#1b3b5f' }}>
                {salesPerson?.displayName || 'You'}
            </div>
            <div style={{ fontSize: 10, color: '#16a34a', marginTop: 1 }}>Linked automatically</div>
        </div>
        <span style={styles.linkedBadge}>✓ </span>
    </div>
);

const ReviewRow = ({ label, value }) => (
    <div style={styles.reviewRow}>
        <span style={styles.reviewLabel}>{label}</span>
        <span style={styles.reviewValue}>{value || '—'}</span>
    </div>
);

const LocationStep = ({ form, setForm }) => {
    const allCountries = useGetCountries();

    const [countryCode, setCountryCode] = useState(form._countryCode || '');
    const [stateCode, setStateCode] = useState(form._stateCode || '');

    const states = useGetStatesByCountryId(countryCode);
    const cities = useGetCitiesByStateId(stateCode, countryCode);

    const countryOptions = (allCountries || []).map((c) => ({
        value: c.isoCode,
        label: c.name,
    }));
    const stateOptions = (states || []).map((s) => ({
        value: s.isoCode,
        label: s.name,
    }));
    const cityOptions = (cities || []).map((c) => ({
        value: c.name,
        label: c.name,
    }));

    const countriesLoading = !allCountries || allCountries.length === 0;
    const statesLoading = !!countryCode && (!states || states.length === 0);
    const citiesLoading = !!stateCode && (!cities || cities.length === 0);

    const handleCountryChange = (e) => {
        const code = e.target.value;
        const found = (allCountries || []).find((c) => c.isoCode === code);
        const name = found?.name || '';

        setCountryCode(code);
        setStateCode('');  
        setForm((f) => ({
            ...f,
            country: name, 
            state: '',
            city: '',
            _countryCode: code, 
            _stateCode: '',
        }));
    };

    const handleStateChange = (e) => {
        const code = e.target.value;
        const found = (states || []).find((s) => s.isoCode === code);
        const name = found?.name || '';

        setStateCode(code);
        setForm((f) => ({
            ...f,
            state: name,
            city: '',
            _stateCode: code,
        }));
    };

    const handleCityChange = (e) => {
        setForm((f) => ({ ...f, city: e.target.value }));
    };

    return (
        <div style={{ display: 'grid', gap: 14 }}>
            <SectionHead icon={<BiMapPin size={14} />} title="Location & Address" />

            {/* Street address */}
            <F label="Street Address">
                <I
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    placeholder="e.g. No 12 Victoria Island"
                    icon={<BiMapPin size={12} />}
                />
            </F>

            <Grid>
                <F label="Country" required>
                    <CascadeSel
                        value={countryCode}
                        onChange={handleCountryChange}
                        options={countryOptions}
                        placeholder={countriesLoading ? 'Loading countries…' : 'Select Country'}
                        loading={countriesLoading}
                        disabled={false}
                    />
                </F>

                {/* ── State — unlocked only after a country is chosen ── */}
                <F label="State">
                    <CascadeSel
                        value={stateCode}
                        onChange={handleStateChange}
                        options={stateOptions}
                        placeholder={
                            !countryCode
                                ? 'Select a country first'
                                : statesLoading
                                ? 'Loading states…'
                                : 'Select State'
                        }
                        loading={statesLoading}
                        disabled={!countryCode}
                    />
                </F>

                {/* ── City — unlocked only after a state is chosen ───── */}
                <F label="City">
                    <CascadeSel
                        value={form.city}
                        onChange={handleCityChange}
                        options={cityOptions}
                        placeholder={
                            !stateCode
                                ? 'Select a state first'
                                : citiesLoading
                                ? 'Loading cities…'
                                : 'Select City'
                        }
                        loading={citiesLoading}
                        disabled={!stateCode}
                    />
                </F>

                {/* ── Zip ──────────────────────────────────────────────  */}
                <F label="Zip / Postal Code">
                    <I
                        value={form.zip}
                        onChange={(e) => setForm((f) => ({ ...f, zip: e.target.value }))}
                        placeholder="100001"
                        icon={<BiHash size={12} />}
                    />
                </F>
            </Grid>

            {/* Live summary of what's been selected */}
            {(form.country || form.state || form.city) && (
                <div style={styles.locationSummary}>
                    <BiMapPin size={12} style={{ color: '#1b3b5f', flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 11, color: '#1b3b5f', fontWeight: 500 }}>
                        {[form.city, form.state, form.country].filter(Boolean).join(', ')}
                    </span>
                </div>
            )}
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   Success overlay
───────────────────────────────────────────────────────────────────────────── */
const SuccessOverlay = ({ data, onDone, onAddAnother }) => (
    <div style={styles.overlayBg}>
        <div style={styles.overlayCard}>
            <div style={styles.successHeader}>
                <div style={styles.successIcon}>
                    <BiCheckCircle size={32} color="#fff" />
                </div>
                <div style={{ fontWeight: 800, fontSize: 20, color: '#fff', marginTop: 10 }}>
                    Customer Created!
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 4 }}>
                    New contact added and linked to your account
                </div>
            </div>
            <div style={{ padding: '22px 24px' }}>
                <div style={styles.successCard}>
                    <div style={styles.successAvatar}>{data.name?.[0]?.toUpperCase() || '?'}</div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
                            {data.name}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            {data.email}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{data.phone}</div>
                    </div>
                </div>

                {data.tempPassword && (
                    <div style={styles.passwordBox}>
                        <div style={styles.passwordLabel}>🔑 Temporary Password</div>
                        <div style={styles.passwordValue}>{data.tempPassword}</div>
                        <div
                            style={{
                                fontSize: 10.5,
                                color: '#92400e',
                                lineHeight: 1.5,
                                marginTop: 4,
                            }}>
                            A welcome email with this password has been sent. The customer can log
                            in and change it.
                        </div>
                    </div>
                )}

                {data.salesPersonName && (
                    <div style={styles.spConfirmBox}>
                        <BiBriefcase size={13} style={{ color: '#1b3b5f', flexShrink: 0 }} />
                        <span style={{ fontSize: 11, color: '#1b3b5f', fontWeight: 600 }}>
                            Linked to: {data.salesPersonName}
                        </span>
                    </div>
                )}

                {data.note && <div style={styles.noteBox}>ℹ️ {data.note}</div>}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                    <button onClick={onAddAnother} style={styles.btnSecondary}>
                        + Add another
                    </button>
                    <button onClick={onDone} style={styles.btnSuccess}>
                        Done
                    </button>
                </div>
            </div>
        </div>
    </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   Error overlay
───────────────────────────────────────────────────────────────────────────── */
const ErrorOverlay = ({ message, onCancel, onRetry, onContinue }) => (
    <div style={styles.overlayBg}>
        <div style={styles.overlayCard}>
            <div style={styles.errorHeader}>
                <div style={styles.errorIcon}>
                    <FiAlertCircle size={26} color="#fff" />
                </div>
                <div style={{ fontWeight: 800, fontSize: 17, color: '#fff', marginTop: 10 }}>
                    Failed to create customer
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>
                    Review the error below
                </div>
            </div>
            <div style={{ padding: '20px 24px' }}>
                <div style={styles.errorBox}>
                    <div style={styles.errorBoxLabel}>Error Details</div>
                    <p style={styles.errorBoxText}>{message}</p>
                </div>
                <div style={styles.warningBox}>
                    <strong>Note:</strong> If Zoho returned an error, the contact may have been
                    partially created. Use <em>Continue anyway</em> to dismiss, or{' '}
                    <em>Try again</em> to re-submit.
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onCancel} style={styles.btnTertiary}>
                        Cancel
                    </button>
                    <button onClick={onContinue} style={styles.btnOutlineGreen}>
                        Continue anyway
                    </button>
                    <button onClick={onRetry} style={styles.btnPrimary}>
                        ↺ Try again
                    </button>
                </div>
            </div>
        </div>
    </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   Main modal
───────────────────────────────────────────────────────────────────────────── */
export default function CreateCustomerModal({ onClose, onSuccess, loggedInSalesPerson }) {
    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(null);

    const blankForm = {
        salutation: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company_name: '',
        dob: '',
        address: '',
        /* human-readable location names — sent to Zoho */
        country: '',
        state: '',
        city: '',
        /* isoCode helpers — only used to drive the cascade, NOT sent to API */
        _countryCode: '',
        _stateCode: '',
        zip: '',
        website: '',
        operationalMode: '',
        notes: '',
    };

    const [form, setForm] = useState(blankForm);

    const set = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
        if (errors[k]) setErrors((er) => ({ ...er, [k]: '' }));
    };

    const validate = (s) => {
        const err = {};
        if (s === 0) {
            if (!form.first_name.trim()) err.first_name = 'Required';
            if (!form.email.trim()) err.email = 'Required';
            if (!form.phone.trim()) err.phone = 'Required';
        }
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const next = () => {
        if (validate(step)) setStep((s) => s + 1);
    };
    const back = () => setStep((s) => s - 1);

    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        const tempPassword = genTempPassword();
        try {
            const salesPersonId = loggedInSalesPerson?.sales_person_id || '';
            const qs = salesPersonId ? `?sales_person_id=${encodeURIComponent(salesPersonId)}` : '';
            const res = await apiService.post({
                url: `/users/create-sales-customer${qs}`,
                payload: buildPayload(form, tempPassword, loggedInSalesPerson),
            });

            const savedUser = res?.data?.user || res?.user || {};
            const contact = res?.data?.contact || res?.contact || {};

            setSaveSuccess({
                name: `${form.first_name} ${form.last_name}`.trim(),
                email: form.email,
                phone: form.phone,
                contact_id: savedUser?.attributes?.zoho_id || contact?.contact_id,
                user_id: savedUser?.id,
                salesPersonName: loggedInSalesPerson?.displayName,
                tempPassword,
            });
            onSuccess?.();
        } catch (err) {
            const raw = err?.response?.data;
            const msg =
                raw?.message || raw?.data?.message || err.message || 'Something went wrong.';
            if (
                msg?.toLowerCase()?.includes('already registered') ||
                msg?.toLowerCase()?.includes('already linked')
            ) {
                setSaveSuccess({
                    name: `${form.first_name} ${form.last_name}`.trim(),
                    email: form.email,
                    phone: form.phone,
                    salesPersonName: loggedInSalesPerson?.displayName,
                    tempPassword,
                    note: 'This email/phone is already registered — existing account found.',
                });
                onSuccess?.();
            } else {
                setSaveError(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    const initials = form.first_name?.[0]?.toUpperCase() || '';

    const stepContent = [
        <div key="identity" style={{ display: 'grid', gap: 14 }}>
            <SectionHead icon={<BiUser size={14} />} title="Personal Information" />
            <Grid>
                <F label="Title">
                    <Sel
                        value={form.salutation}
                        onChange={set('salutation')}
                        options={TITLES.map((t) => ({ value: t, label: t }))}
                        placeholder="Select…"
                    />
                </F>
                <F label="Account Type">
                    <div
                        style={{
                            ...styles.input,
                            display: 'flex',
                            alignItems: 'center',
                            background: '#f8fafc',
                            color: '#374151',
                            cursor: 'not-allowed',
                        }}>
                        Customer
                        <span
                            style={{
                                marginLeft: 'auto',
                                fontSize: 10,
                                background: '#eff6ff',
                                color: '#2563eb',
                                padding: '2px 7px',
                                borderRadius: 6,
                                fontWeight: 600,
                            }}>
                            default
                        </span>
                    </div>
                </F>
                <F label="First Name" required error={errors.first_name}>
                    <I
                        value={form.first_name}
                        onChange={set('first_name')}
                        placeholder="First name"
                        icon={<BiUser size={12} />}
                    />
                </F>
                <F label="Last Name">
                    <I value={form.last_name} onChange={set('last_name')} placeholder="Last name" />
                </F>
                <F label="Email Address" required error={errors.email}>
                    <I
                        value={form.email}
                        onChange={set('email')}
                        placeholder="customer@email.com"
                        type="email"
                        icon={<BiMailSend size={12} />}
                    />
                </F>
                <F label="Phone Number" required error={errors.phone}>
                    <I
                        value={form.phone}
                        onChange={set('phone')}
                        placeholder="+234 080…"
                        icon={<BiPhone size={12} />}
                    />
                </F>
                <F label="Company Name">
                    <I
                        value={form.company_name}
                        onChange={set('company_name')}
                        placeholder="Optional"
                        icon={<LuBuilding2 size={12} />}
                    />
                </F>
                <F label="Date of Birth">
                    <I
                        value={form.dob}
                        onChange={set('dob')}
                        type="date"
                        icon={<BiCalendar size={12} />}
                    />
                </F>
                <F label="Website" col="span 2">
                    <I
                        value={form.website}
                        onChange={set('website')}
                        placeholder="https://…"
                        type="url"
                        icon={<BiGlobe size={12} />}
                    />
                </F>
            </Grid>
            <F label="Notes">
                <textarea
                    value={form.notes}
                    onChange={set('notes')}
                    rows={2}
                    placeholder="Any notes about this customer…"
                    style={{
                        ...styles.input,
                        height: 'auto',
                        padding: '8px 10px',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                    }}
                />
            </F>
        </div>,

        /* ── Step 1: Location (component with its own hooks) ─────────── */
        <LocationStep key="location" form={form} setForm={setForm} />,

        /* ── Step 2: Profile ──────────────────────────────────────────── */
        <div key="profile" style={{ display: 'grid', gap: 16 }}>
            <SectionHead icon={<BiShoppingBag size={14} />} title="Profile & Linkage" />

            <F label="Operational Mode">
                <Sel
                    value={form.operationalMode}
                    onChange={set('operationalMode')}
                    options={OPERATIONAL_MODES.map((o) => ({ value: o, label: o }))}
                    placeholder="Select…"
                />
            </F>

            <F label="Referral Source">
                <div
                    style={{
                        ...styles.input,
                        display: 'flex',
                        alignItems: 'center',
                        background: '#f8fafc',
                        color: '#374151',
                        cursor: 'not-allowed',
                        gap: 6,
                    }}>
                    <BiBriefcase size={12} style={{ color: '#94a3b8', flexShrink: 0 }} />
                    Salesperson
                    <span
                        style={{
                            marginLeft: 'auto',
                            fontSize: 10,
                            background: '#eff6ff',
                            color: '#2563eb',
                            padding: '2px 7px',
                            borderRadius: 6,
                            fontWeight: 600,
                        }}>
                        auto-set
                    </span>
                </div>
            </F>

            <F label="Linked Salesperson">
                <LockedSP salesPerson={loggedInSalesPerson} />
            </F>

            <div style={styles.infoCallout}>
                <BiBriefcase size={13} style={{ color: '#1b3b5f', flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 11, color: '#1b3b5f', lineHeight: 1.55 }}>
                    This customer will be automatically linked to you (
                    <strong>{loggedInSalesPerson?.displayName || 'you'}</strong>) as salesperson. This cannot be changed after creation.
                </div>
            </div>
        </div>,

        <div key="review" style={{ display: 'grid', gap: 14 }}>
            <SectionHead icon={<BiCheckCircle size={14} />} title="Review & Confirm" />

            <div style={styles.reviewCard}>
                <div style={styles.reviewCardHeader}>
                    <div style={styles.reviewAvatar}>
                        {initials || <BiUser size={18} color="#fff" />}
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1b3b5f' }}>
                            {[form.salutation, form.first_name, form.last_name]
                                .filter(Boolean)
                                .join(' ') || '—'}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{form.email}</div>
                        {form.company_name && (
                            <div style={{ fontSize: 10, color: '#94a3b8' }}>
                                {form.company_name}
                            </div>
                        )}
                    </div>
                    <span style={styles.individualBadge}>Customer</span>
                </div>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 0,
                        borderTop: '1px solid #f1f5f9',
                        paddingTop: 8,
                    }}>
                    <ReviewRow label="Phone" value={form.phone} />
                    <ReviewRow label="City" value={form.city} />
                    <ReviewRow label="State" value={form.state} />
                    <ReviewRow label="Country" value={form.country} />
                    <ReviewRow label="Op. Mode" value={form.operationalMode} />
                </div>
            </div>

            <div style={styles.spReviewBox}>
                <div
                    style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: '#92400e',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        marginBottom: 8,
                    }}>
                    Salesperson Linkage
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ ...styles.spAvatar, width: 32, height: 32, fontSize: 13 }}>
                        {loggedInSalesPerson?.first_name?.[0]?.toUpperCase() || 'S'}
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 12, color: '#1b3b5f' }}>
                            {loggedInSalesPerson?.displayName || '—'}
                        </div>
                        <div style={{ fontSize: 10, color: '#64748b' }}>
                            ID: {loggedInSalesPerson?.sales_person_id || '—'}
                        </div>
                    </div>
                    <span style={{ marginLeft: 'auto', ...styles.linkedBadge }}>
                        Will be linked
                    </span>
                </div>
            </div>

            <div style={styles.confirmCallout}>
                Everything looks good? Click <strong>Create Customer</strong> to save and link to
                your account.
            </div>
        </div>,
    ];

    /* ── Modal shell ─────────────────────────────────────────────────── */
    return (
        <>
            <div
                style={styles.backdrop}
                onClick={(e) => {
                    if (e.target === e.currentTarget) onClose();
                }}>
                <div style={styles.modal}>
                    {/* Header */}
                    <div style={styles.header}>
                        <div>
                            <div style={styles.headerTitle}>Add New Customer</div>
                            <div style={styles.headerSub}>
                                Step {step + 1} of {STEPS.length} — {STEPS[step]}
                            </div>
                        </div>
                        {/* Logged-in salesperson pill */}
                        <div style={styles.spPill}>
                            <div
                                style={{ ...styles.spAvatar, width: 22, height: 22, fontSize: 10 }}>
                                {loggedInSalesPerson?.first_name?.[0]?.toUpperCase() || 'S'}
                            </div>
                            <span style={{ fontSize: 10.5, fontWeight: 600, color: '#1b3b5f' }}>
                                {loggedInSalesPerson?.displayName || 'Salesperson'}
                            </span>
                        </div>
                        <button onClick={onClose} style={styles.closeBtn}>
                            <BiX size={14} />
                        </button>
                    </div>

                    <StepBar current={step} />

                    <div style={styles.body}>{stepContent[step]}</div>

                    {/* Footer nav */}
                    <div style={styles.footer}>
                        <button
                            onClick={back}
                            disabled={step === 0}
                            style={{
                                ...styles.btnBack,
                                opacity: step === 0 ? 0.35 : 1,
                                cursor: step === 0 ? 'not-allowed' : 'pointer',
                            }}>
                            <BsArrowLeft size={12} /> Back
                        </button>

                        <div style={{ display: 'flex', gap: 4 }}>
                            {STEPS.map((_, i) => (
                                <div
                                    key={i}
                                    style={{
                                        width: i === step ? 20 : 7,
                                        height: 7,
                                        borderRadius: 4,
                                        background:
                                            i < step
                                                ? '#16a34a'
                                                : i === step
                                                ? '#1b3b5f'
                                                : '#e2e8f0',
                                        transition: 'all .25s',
                                    }}
                                />
                            ))}
                        </div>

                        {step < STEPS.length - 1 ? (
                            <button onClick={next} style={styles.btnNext}>
                                Next <BiChevronRight size={12} />
                            </button>
                        ) : (
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                style={{
                                    ...styles.btnSave,
                                    background: saving ? '#94a3b8' : '#16a34a',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                }}>
                                {saving ? (
                                    'Saving…'
                                ) : (
                                    <>
                                        <BiCheckCircle size={12} /> Create Customer
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {saveSuccess && (
                <SuccessOverlay
                    data={saveSuccess}
                    onDone={() => {
                        setSaveSuccess(null);
                        onClose();
                    }}
                    onAddAnother={() => {
                        setSaveSuccess(null);
                        setStep(0);
                        setForm(blankForm);
                        onSuccess?.();
                    }}
                />
            )}

            {saveError && (
                <ErrorOverlay
                    message={saveError}
                    onCancel={() => setSaveError(null)}
                    onContinue={() => {
                        setSaveError(null);
                        onSuccess?.();
                        onClose();
                    }}
                    onRetry={() => {
                        setSaveError(null);
                        handleSave();
                    }}
                />
            )}
        </>
    );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Styles
───────────────────────────────────────────────────────────────────────────── */
const styles = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 40,
        background: 'rgba(15,23,42,0.55)',
        backdropFilter: 'blur(4px)',
    },
    modal: {
        background: '#fff',
        borderRadius: 14,
        width: 'min(96vw, 640px)',
        maxHeight: '88vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
        overflow: 'hidden',
    },
    header: {
        padding: '16px 20px 14px',
        borderBottom: '1px solid #f1f5f9',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        gap: 10,
    },
    headerTitle: { fontWeight: 800, fontSize: 15, color: '#1b3b5f' },
    headerSub: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
    spPill: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 20,
        padding: '4px 10px',
        marginLeft: 'auto',
    },
    closeBtn: {
        background: 'none',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        padding: '5px 8px',
        cursor: 'pointer',
        color: '#64748b',
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
    },
    stepBar: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 20px',
        background: '#f8fafc',
        borderBottom: '1px solid #e5e7eb',
        gap: 0,
        flexShrink: 0,
    },
    stepCircle: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 700,
        flexShrink: 0,
    },
    body: { flex: 1, overflowY: 'auto', padding: '20px 24px' },
    footer: {
        padding: '14px 24px',
        borderTop: '1px solid #f1f5f9',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        background: '#fafafa',
    },
    sectionHead: {
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '8px 0 6px',
        borderBottom: '1px solid #f1f5f9',
        marginBottom: 4,
    },
    sectionTitle: {
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        color: '#1b3b5f',
        letterSpacing: '0.05em',
    },
    label: { display: 'block', fontSize: 10.5, fontWeight: 600, color: '#64748b', marginBottom: 4 },
    errText: { fontSize: 10, color: '#dc2626', marginTop: 3 },
    input: {
        height: 36,
        border: '1px solid #e2e8f0',
        borderRadius: 7,
        padding: '0 10px',
        fontSize: 12,
        color: '#374151',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        background: '#fff',
    },
    iconWrap: {
        position: 'absolute',
        left: 9,
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8',
        display: 'flex',
        alignItems: 'center',
    },
    lockedSP: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        border: '1px solid #bbf7d0',
        borderRadius: 7,
        padding: '10px 14px',
        background: '#f0fdf4',
    },
    spAvatar: {
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: '#1b3b5f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: 12,
        flexShrink: 0,
    },
    linkedBadge: {
        marginLeft: 'auto',
        fontSize: 10,
        background: '#dcfce7',
        color: '#166534',
        padding: '2px 8px',
        borderRadius: 10,
        fontWeight: 700,
    },
    infoCallout: {
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 8,
        padding: '11px 14px',
    },
    confirmCallout: {
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: 7,
        padding: '10px 14px',
        fontSize: 11,
        color: '#166534',
    },
    locationSummary: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 7,
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: 7,
        padding: '8px 12px',
    },
    reviewCard: {
        background: '#f8fafc',
        border: '1px solid #e4eaf2',
        borderRadius: 10,
        padding: 14,
    },
    reviewCardHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
    reviewAvatar: {
        width: 42,
        height: 42,
        borderRadius: 8,
        background: '#1b3b5f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: 18,
        flexShrink: 0,
    },
    individualBadge: {
        marginLeft: 'auto',
        background: '#eff6ff',
        borderRadius: 6,
        padding: '3px 9px',
        fontSize: 10,
        color: '#2563eb',
        fontWeight: 600,
    },
    reviewRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '6px 0',
        borderBottom: '1px solid #f1f5f9',
    },
    reviewLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 600, minWidth: 70, flexShrink: 0 },
    reviewValue: { fontSize: 11, color: '#1e293b', fontWeight: 500, textAlign: 'right' },
    spReviewBox: {
        background: '#fefce8',
        border: '1.5px solid #fde68a',
        borderRadius: 8,
        padding: '12px 14px',
    },
    /* Buttons */
    btnBack: {
        padding: '8px 16px',
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 7,
        fontSize: 11,
        fontWeight: 600,
        color: '#64748b',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
    },
    btnNext: {
        padding: '8px 20px',
        background: '#1b3b5f',
        color: '#fff',
        border: 'none',
        borderRadius: 7,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
    },
    btnSave: {
        padding: '8px 20px',
        color: '#fff',
        border: 'none',
        borderRadius: 7,
        fontSize: 11,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
    },
    /* Overlays */
    overlayBg: {
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(2px)',
    },
    overlayCard: {
        background: '#fff',
        borderRadius: 14,
        width: 'min(92vw,460px)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
        overflow: 'hidden',
    },
    successHeader: {
        background: 'linear-gradient(135deg,#16a34a,#15803d)',
        padding: '26px 24px 22px',
        textAlign: 'center',
    },
    successIcon: {
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 10px',
    },
    successCard: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: '#f8fafc',
        border: '1px solid #e4eaf2',
        borderRadius: 8,
        padding: '12px 14px',
        marginBottom: 14,
    },
    successAvatar: {
        width: 40,
        height: 40,
        borderRadius: 8,
        background: '#1b3b5f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: 17,
        flexShrink: 0,
    },
    passwordBox: {
        background: '#fefce8',
        border: '1.5px solid #fde68a',
        borderRadius: 8,
        padding: '13px 15px',
        marginBottom: 12,
    },
    passwordLabel: {
        fontSize: 10.5,
        fontWeight: 700,
        color: '#92400e',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: 6,
    },
    passwordValue: {
        fontFamily: 'monospace',
        fontSize: 20,
        fontWeight: 800,
        color: '#1b3b5f',
        letterSpacing: '0.1em',
        userSelect: 'all',
    },
    spConfirmBox: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 7,
        padding: '9px 13px',
        marginBottom: 8,
    },
    noteBox: {
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 7,
        padding: '9px 13px',
        marginBottom: 8,
        fontSize: 11,
        color: '#1d4ed8',
        lineHeight: 1.5,
    },
    errorHeader: {
        background: 'linear-gradient(135deg,#dc2626,#b91c1c)',
        padding: '22px 24px 18px',
        textAlign: 'center',
    },
    errorIcon: {
        width: 48,
        height: 48,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.18)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 8px',
    },
    errorBox: {
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 8,
        padding: '13px 15px',
        marginBottom: 12,
        maxHeight: 150,
        overflowY: 'auto',
    },
    errorBoxLabel: {
        fontSize: 10,
        fontWeight: 700,
        color: '#991b1b',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 6,
    },
    errorBoxText: {
        fontSize: 12,
        color: '#7f1d1d',
        lineHeight: 1.65,
        margin: 0,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
    },
    warningBox: {
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 7,
        padding: '9px 13px',
        marginBottom: 14,
        fontSize: 11,
        color: '#92400e',
        lineHeight: 1.5,
    },
    btnSecondary: {
        padding: '9px 18px',
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 7,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        color: '#374151',
    },
    btnSuccess: {
        padding: '9px 24px',
        background: '#16a34a',
        color: '#fff',
        border: 'none',
        borderRadius: 7,
        fontSize: 12,
        fontWeight: 700,
        cursor: 'pointer',
    },
    btnTertiary: {
        padding: '8px 14px',
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 7,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        color: '#64748b',
    },
    btnOutlineGreen: {
        padding: '8px 14px',
        background: '#fff',
        border: '1px solid #bbf7d0',
        borderRadius: 7,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        color: '#16a34a',
    },
    btnPrimary: {
        padding: '8px 16px',
        background: '#1b3b5f',
        color: '#fff',
        border: 'none',
        borderRadius: 7,
        fontSize: 11,
        fontWeight: 700,
        cursor: 'pointer',
    },
};
